<template>
	<div class="flex flex-wrap w-full">
		<div class="w-full lg:w-2/3 lg:pr-8">
			<div class="mb-4">
				<div class="flex justify-between">
					<div class="flex w-full">
						<div class="flex flex-wrap">
							<BaseSelect
								ref="BaseInput.isRecordedByPolice"
								:required="false"
								:label="$t('recorded_by_police')"
								:options="policeRecordedOptions"
								v-model="isRecordedByPoliceOption"
								@input="updatePoliceRecorded"
							/>
						</div>

						<div v-if="isRecordedByPoliceOption && isRecordedByPoliceOption.value && !accidentReport" class="pt-8 ml-5">
							<div class="flex w-full">
								<BaseFileUploader
									@input="uploadAccidentReport"
									@update:filename="accidentReportFileName = $event"
									accept="image/png, image/jpeg, application/pdf"
									:multiple="false"
								>
									<BaseButton>
										<font-awesome-icon :icon="['fas', 'upload']" class="text-gray-700 mr-2" />

										{{ $t('upload_accident_document') }}
									</BaseButton>
								</BaseFileUploader>
							</div>
						</div>
					</div>
				</div>

				<div class="flex flex-wrap">
					<BaseDatepicker
						ref="BaseInput.date"
						class="w-full md:w-1/2 pr-0 md:pr-4"
						:required="false"
						:label="$t('accident_date')"
						v-model="date"
						:max_date="new Date()"
						@input="updateValue"
						@select="updateValue"
					/>

					<TimeInput ref="BaseInput.time" class="w-full md:w-1/2" v-model="time" @input="updateValue" />
				</div>

				<div class="flex flex-wrap">
					<BaseInput
						ref="BaseInput.accidentLocation"
						class="w-full md:w-1/2 pr-0 md:pr-4"
						:required="false"
						:label="$t('accident_location')"
						:placeholder="$t('accident_location')"
						mask="AXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
						type="text"
						v-model="accidentLocation"
						@input="updateValue"
					/>

					<BaseSelect
						ref="BaseInput.isPersonInjured"
						class="w-full md:w-1/2"
						:required="false"
						:label="$t('driver_passenger_injured')"
						:options="injuredOptions"
						v-model="isPersonInjuredOption"
						@input="updateValue"
						@change.native="updateValue"
					/>
				</div>
			</div>
		</div>

		<div v-if="accidentReport" class="w-full lg:w-1/3 mt-8 lg:mt-0">
			<div class="accident-report-preview">
				<div class="w-full">
					<div v-if="!isPdfTooLong" class="w-full">
						<div v-if="!isAccidentReportPdf" class="w-full flex flex-col">
							<div class="flex-grow flex items-start justify-center overflow-hidden">
								<BaseImage
									:src="accidentReport.base64content"
									:alt="$t('accident_document')"
									:rotation="rotation"
									:show-rounded="false"
									@tempDoc="setRotatedImage"
									class="w-full"
								/>
							</div>

							<div class="flex items-center justify-end mt-4 flex-shrink-0">
								<div class="mr-4">
									{{ $t('rotate_image_hint') }}
								</div>

								<div @click="rotateImage" class="h-8 cursor-pointer">
									<font-awesome-icon :icon="['fas', 'sync-alt']" class="text-connectProBlue" size="lg" />
								</div>
							</div>
						</div>

						<div v-else ref="pdfPreviewContainer" class="pdf-preview-container">
							<iframe :src="pdfPreviewUrl" class="pdf-preview-frame" type="application/pdf" :height="pdfPreviewHeight" />
						</div>
					</div>

					<div v-else class="text-red-700 px-4 py-2 font-bold">
						{{ $t('too_big_for_preview') }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import TimeInput from '@/components/forms/inputs/TimeInput';
import BaseFileUploader from '@/components/forms/inputs/BaseFileUploader';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseImage from '@/components/forms/BaseImage';

import Validate from '@/mixins/Validate';

import { getTimeWithLeadingNull, getYesNoOption, getYesNoOptions } from '@/plugins/Utils';

export default {
	name: 'DamageAssessorCaseAccidentDataForm',

	components: {
		BaseInput,
		BaseSelect,
		BaseDatepicker,
		TimeInput,
		BaseFileUploader,
		BaseButton,
		BaseImage,
	},

	mixins: [Validate],

	props: {
		value: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			date: null,
			time: null,
			accidentLocation: null,
			policeRecordedOptions: getYesNoOptions(),
			injuredOptions: getYesNoOptions(),
			isRecordedByPoliceOption: null,
			isPersonInjuredOption: null,
			accidentReport: null,
			accidentReportFileName: null,
			pdfPreviewHeight: 500,
			rotation: 0,
		};
	},
	computed: {
		isAccidentReportPdf() {
			return (this.accidentReport?.extension ?? '').toLowerCase() === 'pdf';
		},
		pdfPreviewUrl() {
			if (!this.accidentReport || !this.isAccidentReportPdf || !this.accidentReport.base64content) {
				return null;
			}
			return this.accidentReport.base64content + '#toolbar=0&navpanes=0&scrollbar=0';
		},
		isPdfTooLong() {
			if (!this.accidentReport?.base64content) {
				return false;
			}
			return this.accidentReport.base64content.length > 10000000;
		},
	},
	mounted() {
		this.loadStartValue();
		this.$nextTick(() => {
			this.updatePdfPreviewHeight();
		});
		window.addEventListener('resize', this.updatePdfPreviewHeight);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updatePdfPreviewHeight);
	},
	methods: {
		loadStartValue() {
			if (!this.value) {
				return;
			}

			this.date = this.value.date ? new Date(this.value.date * 1000) : null;
			this.time = null;

			if (this.date && this.value.damageTimeSetByUser) {
				this.time = getTimeWithLeadingNull(this.date.getHours()) + ':' + getTimeWithLeadingNull(this.date.getMinutes());
			}

			this.accidentLocation = this.value.accidentLocation ?? null;
			this.isRecordedByPoliceOption = getYesNoOption(this.value.isRecordedByPolice);
			this.isPersonInjuredOption = getYesNoOption(this.value.isPersonInjured);
			this.accidentReport = this.value.accidentReport ?? null;
			this.rotation = 0;

			if (this.accidentReport && this.isAccidentReportPdf) {
				this.$nextTick(() => {
					this.updatePdfPreviewHeight();
				});
			}
		},
		updatePoliceRecorded(option) {
			this.isRecordedByPoliceOption = option;
			if (!option || !option.value) {
				this.accidentReport = null;
				this.accidentReportFileName = null;
				this.rotation = 0;
			}
			this.updateValue();
		},
		updateValue() {
			const value = {
				date: this.getAccidentTimestamp(),
				damageTimeSetByUser: this.hasAccidentTime(),
				accidentLocation: this.accidentLocation ?? null,
				isRecordedByPolice: this.isRecordedByPoliceOption ? this.isRecordedByPoliceOption.value : null,
				isPersonInjured: this.isPersonInjuredOption ? this.isPersonInjuredOption.value : null,
				accidentReport: this.accidentReport,
			};
			this.$emit('input', value);
			this.$emit('updateValue', value);
		},
		getAccidentTimestamp() {
			if (!this.date) {
				return null;
			}
			const date = new Date(this.date);
			if (this.hasAccidentTime()) {
				const timeParts = this.time.split(':');
				date.setHours(parseInt(timeParts[0], 10), parseInt(timeParts[1], 10), 0, 0);
			} else {
				date.setHours(0, 0, 0, 0);
			}
			return Math.floor(date.getTime() / 1000);
		},
		hasAccidentTime() {
			return !!(this.date && this.time);
		},
		uploadAccidentReport(base64Content) {
			this.accidentReport = this.createMediaDto(base64Content);
			this.rotation = 0;
			if (!this.isAccidentReportPdf) {
				const img = new Image();
				// eslint-disable-next-line @typescript-eslint/no-this-alias
				const context = this;
				img.onload = function() {
					if (img.width > img.height) {
						context.rotation = context.rotation === 270 ? 0 : context.rotation + 90;
					}
				};
				img.src = this.accidentReport.base64content;
			}
			this.updateValue();
			if (this.isAccidentReportPdf) {
				this.$nextTick(() => {
					this.updatePdfPreviewHeight();
				});
			}
		},

		removeAccidentReport() {
			this.accidentReport = null;
			this.accidentReportFileName = null;
			this.rotation = 0;
			this.updateValue();
		},

		createMediaDto(base64Content) {
			const mimeType = this.getMimeType(base64Content);
			let type = 'document';
			let extension = 'pdf';
			switch (mimeType) {
				case 'image/jpeg':
					type = 'image';
					extension = 'jpg';
					break;
				case 'image/png':
					type = 'image';
					extension = 'png';
					break;
				case 'application/pdf':
					type = 'document';
					extension = 'pdf';
					break;
			}
			return {
				type,
				extension,
				base64content: base64Content,
			};
		},
		getMimeType(base64Content) {
			if (typeof base64Content !== 'string' || !base64Content.startsWith('data:')) {
				return '';
			}
			const endIndex = base64Content.indexOf(';');
			if (endIndex === -1) {
				return '';
			}
			return base64Content.substring(5, endIndex);
		},
		updatePdfPreviewHeight() {
			this.$nextTick(() => {
				const container = this.$refs.pdfPreviewContainer;
				if (!container) {
					return;
				}
				const width = container.clientWidth;
				if (!width) {
					return;
				}

				/*
				 * A4 portrait:
				 *
				 * width  = 210 mm
				 * height = 297 mm
				 */
				this.pdfPreviewHeight = Math.round(width * (297 / 210));
			});
		},
		rotateImage() {
			this.rotation = this.rotation === 270 ? 0 : this.rotation + 90;
		},
		setRotatedImage(image) {
			if (!this.accidentReport || !image) {
				return;
			}

			this.accidentReport = {
				...this.accidentReport,
				base64content: image,
			};

			this.rotation = 0;
			this.updateValue();
		},
	},
};
</script>

<style scoped>
.accident-report-preview {
	width: 100%;
}

.pdf-preview-container {
	width: 100%;
	max-width: 100%;
	overflow: hidden;
}

.pdf-preview-frame {
	display: block;
	width: 100%;
	min-width: 0;
	border: 0;
}
</style>
